// BEGIN FILE: src/utils/astroEngine.ts 
import * as Astronomy from 'astronomy-engine';
import {
  AyanamsaProvider,
  constantAyanamsa,
  norm360,
  julianDay,
  lahiriAyanamsaApprox
} from './ayanamsa';
import { DateTime } from 'luxon';
import {
  saptavargajaBala,
  ojayugmarasyamsaBala,
  kendraBala,
  drekkanaBala,
  varaBala,
  horaBala,
  ayanaBala
} from './shadbalaComponents';

// src/utils/astroEngine.ts
export const PLANET_ORDER = ['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn'] as const;
export type PlanetOrderName = typeof PLANET_ORDER[number];

/* ---------- Names & helpers ---------- */

export const NAKSHATRA_NAMES = [
  "Ashwini","Bharani","Krittika","Rohini","Mrigashira","Ardra",
  "Punarvasu","Pushya","Ashlesha","Magha","Purva Phalguni","Uttara Phalguni",
  "Hasta","Chitra","Swati","Vishakha","Anuradha","Jyeshtha","Mula",
  "Purva Ashadha","Uttara Ashadha","Shravana","Dhanishta","Shatabhisha",
  "Purva Bhadrapada","Uttara Bhadrapada","Revati"
] as const;

export const RASHI_NAMES = [
  "Aries","Taurus","Gemini","Cancer","Leo","Virgo",
  "Libra","Scorpio","Sagittarius","Capricorn","Aquarius","Pisces"
] as const;

export type RashiName = typeof RASHI_NAMES[number];
export type NakshatraName = typeof NAKSHATRA_NAMES[number];

export interface ObserverInput {
  isoDate: string;       // ISO instant (UTC)
  latitude: number;
  longitude: number;
  ayanamsa?: AyanamsaProvider; // optional; defaults to constant ~Lahiri
}

function getTime(date: Date): Astronomy.AstroTime {
  return Astronomy.MakeTime(date);
}

/** Convert a geocentric EQJ vector to true ecliptic-of-date longitude (tropical, deg). */
function eqjVectorToEctLongitudeDeg(vec: Astronomy.Vector, time: Astronomy.AstroTime): number {
  const rotEqjToEct = Astronomy.Rotation_EQJ_ECT(time);
  const ectVec = Astronomy.RotateVector(rotEqjToEct, vec);
  const ect = Astronomy.SphereFromVector(ectVec);
  return norm360(ect.lon);
}

/** Tropical ecliptic longitude (deg) for a body, geocentric, true-of-date. */
function tropicalEclipticLongitude(body: Astronomy.Body, time: Astronomy.AstroTime): number {
  const eqj = Astronomy.GeoVector(body, time, true); // aberration = true
  return eqjVectorToEctLongitudeDeg(eqj, time);
}

/** Siderealize a tropical ecliptic longitude using a provider. */
function toSidereal(tropicalLon: number, date: Date, ayanamsa: AyanamsaProvider): number {
  return norm360(tropicalLon - ayanamsa(date));
}

function indicesFromSiderealLongitude(siderealLon: number) {
  const nakIndex0 = Math.floor(siderealLon / (360 / 27)); // 0..26
  const rashiIndex0 = Math.floor(siderealLon / 30);       // 0..11
  return {
    nakIndex1: nakIndex0 + 1,
    rashiIndex1: rashiIndex0 + 1,
    nakshatra: NAKSHATRA_NAMES[nakIndex0],
    rashi: RASHI_NAMES[rashiIndex0],
  } as {
    nakIndex1: number; rashiIndex1: number;
    nakshatra: NakshatraName; rashi: RashiName;
  };
}

/** Utility: format DD°MM′SS″ inside-sign */
export function formatLonWithSign(lonDeg: number) {
  const lon = norm360(lonDeg);
  const signIndex0 = Math.floor(lon / 30);
  const signName = RASHI_NAMES[signIndex0];
  const inSign = lon - signIndex0 * 30;
  let d = Math.floor(inSign);
  let mFloat = (inSign - d) * 60;
  let m = Math.floor(mFloat);
  let s = Math.round((mFloat - m) * 60);
  if (s === 60) { s = 0; m += 1; }
  if (m === 60) { m = 0; d += 1; }
  if (d === 30) { d = 0; }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${signName} ${d}°${pad(m)}′${pad(s)}″`;
}

/* ---------- Moon & Ascendant ---------- */

export function getMoonNakshatraAndRashi(
  isoDate: string,
  _latitude: number,
  _longitude: number,
  ayanamsaProvider: AyanamsaProvider = constantAyanamsa()
) {
  const date = new Date(isoDate);
  const time = getTime(date);
  const tropicalLon = tropicalEclipticLongitude(Astronomy.Body.Moon, time);
  const sidLon = toSidereal(tropicalLon, date, ayanamsaProvider);
  const { nakIndex1, rashiIndex1, nakshatra, rashi } = indicesFromSiderealLongitude(sidLon);
  return {
    nakshatra,
    nakIndex: nakIndex1,
    moonSign: rashi,
    moonSignIndex: rashiIndex1,
    siderealLongitudeDeg: sidLon,
    siderealLongitudeFormatted: `${sidLon.toFixed(2)}°`,
  };
}

// tiny vec helpers
function makeVec(x: number, y: number, z: number, t: Astronomy.AstroTime): Astronomy.Vector {
  return new Astronomy.Vector(x, y, z, t);
}
function dotV(a: Astronomy.Vector, b: Astronomy.Vector): number { return a.x*b.x + a.y*b.y + a.z*b.z; }
function crossV(a: Astronomy.Vector, b: Astronomy.Vector): Astronomy.Vector {
  return makeVec(a.y*b.z - a.z*b.y, a.z*b.x - a.x*b.z, a.x*b.y - a.y*b.x, a.t);
}
function normV(v: Astronomy.Vector): Astronomy.Vector {
  const m = Math.hypot(v.x, v.y, v.z);
  return makeVec(v.x/m, v.y/m, v.z/m, v.t);
}

/** Ascendant sign & nakshatra (sidereal), plus numeric/pretty longitude. */
export function getAscendantSignAndNakshatra(
  isoDate: string,
  latitude: number,
  longitude: number,
  ayanamsaProvider: AyanamsaProvider = constantAyanamsa()
) {
  const date = new Date(isoDate);
  const time = Astronomy.MakeTime(date);
  const observer = new Astronomy.Observer(latitude, longitude, 0);

  // Rotations
  const rotHOR_EQD = Astronomy.Rotation_HOR_EQD(time, observer);
  const rotEQD_ECT = Astronomy.Rotation_EQD_ECT(time);
  const rotECT_EQD = Astronomy.Rotation_ECT_EQD(time);

  // Horizon plane normal in EQD = local zenith (HOR z-hat → EQD)
  const zHatHOR = makeVec(0, 0, 1, time);
  const zenithEQD = Astronomy.RotateVector(rotHOR_EQD, zHatHOR);

  // Ecliptic plane normal in EQD = ecliptic north (ECT z-hat → EQD)
  const zHatECT = makeVec(0, 0, 1, time);
  const eclNormalEQD = Astronomy.RotateVector(rotECT_EQD, zHatECT);

  // Intersection line L = eclNormal × zenith (in EQD)
  let L = crossV(eclNormalEQD, zenithEQD);

  // Choose eastward intersection using an EAST vector from horizon frame
  const eastHOR = Astronomy.VectorFromHorizon({ lat: 0, lon: 90, dist: 1 }, time, 'normal');
  const eastEQD = Astronomy.RotateVector(rotHOR_EQD, eastHOR);
  if (dotV(L, eastEQD) < 0) L = makeVec(-L.x, -L.y, -L.z, L.t);
  const ascEQD = normV(L);

  // To ecliptic-of-date → read tropical longitude
  const ascECT = Astronomy.RotateVector(rotEQD_ECT, ascEQD);
  const ascSphere = Astronomy.SphereFromVector(ascECT);
  const tropicalLon = norm360(ascSphere.lon);

  // Siderealize
  const sidLon = norm360(tropicalLon - ayanamsaProvider(date));

  // Indices/names
  const nakIndex = Math.floor(sidLon / (360 / 27));
  const rashiIndex = Math.floor(sidLon / 30);

  return {
    ascendantSign: RASHI_NAMES[rashiIndex],
    ascendantSignIndex: rashiIndex + 1,
    ascendantNakshatra: NAKSHATRA_NAMES[nakIndex],
    nakIndex: nakIndex + 1,
    siderealLongitudeDeg: sidLon,
    siderealLongitudeFormatted: `${sidLon.toFixed(2)}°`,
  };
}

/* ---------- Planet longitudes & motion ---------- */

export type GrahaName = 'Sun'|'Moon'|'Mercury'|'Venus'|'Mars'|'Jupiter'|'Saturn';
export type GrahaLongitudes = Record<GrahaName, number>;

export function getGrahaLongitudesSidereal(
  { isoDate }: ObserverInput,
  ayanamsaProvider: AyanamsaProvider = constantAyanamsa()
): GrahaLongitudes {
  const date = new Date(isoDate);
  const time = getTime(date);

  const bodies: [GrahaName, Astronomy.Body][] = [
    ['Sun', Astronomy.Body.Sun],
    ['Moon', Astronomy.Body.Moon],
    ['Mercury', Astronomy.Body.Mercury],
    ['Venus', Astronomy.Body.Venus],
    ['Mars', Astronomy.Body.Mars],
    ['Jupiter', Astronomy.Body.Jupiter],
    ['Saturn', Astronomy.Body.Saturn],
  ];

  const result = {} as GrahaLongitudes;
  for (const [name, body] of bodies) {
    const trop = tropicalEclipticLongitude(body, time);
    result[name] = toSidereal(trop, date, ayanamsaProvider);
  }
  return result;
}

/** Whole-Sign cusps starting from Lagna (1..12), in sidereal degrees. */
export function getWholeSignCuspsSidereal(ascendantSiderealLongitudeDeg: number): number[] {
  const ascSignStart = Math.floor(ascendantSiderealLongitudeDeg / 30) * 30; // 0°, 30°, …
  const cusps: number[] = [];
  for (let i = 0; i < 12; i++) {
    cusps.push(norm360(ascSignStart + i * 30));
  }
  return cusps;
}

/** Equal-house cusps starting from numeric Lagna (deg). */
export function getEqualHouseCuspsSidereal(
  ascendantSiderealLongitudeDeg: number
): number[] {
  // In Bhava Chalit (Equal House), the cusp is the MIDPOINT of the house
  // So we need to return the START of each house, which is 15° before the midpoint
  const cusps: number[] = [];
  for (let i = 0; i < 12; i++) {
    // Each house midpoint (cusp) is at ASC + i*30
    const midpoint = norm360(ascendantSiderealLongitudeDeg + i * 30);
    // House starts 15° before midpoint
    const houseStart = norm360(midpoint - 15);
    cusps.push(houseStart);
  }
  return cusps;
}

/* ---------- Nakshatra pada ---------- */

export function getNakshatraPadaFromLongitude(sidLonDeg: number) {
  const NAK_SIZE = 360 / 27;      // 13.333...°
  const PADA_SIZE = NAK_SIZE / 4; // 3.333...°
  const posInNak = ((sidLonDeg % NAK_SIZE) + NAK_SIZE) % NAK_SIZE;
  const padaIndex0 = Math.floor(posInNak / PADA_SIZE);
  const degWithinPada = posInNak - padaIndex0 * PADA_SIZE; // 0..3.333...°
  return {
    pada: padaIndex0 + 1,
    degWithinPada,
  };
}

/* ---------- Motion (retrograde) ---------- */

export type GrahaMotion = Record<GrahaName, { lon: number; retrograde: boolean; speedDegPerDay: number }>;

function signedAngleDeltaDeg(a2: number, a1: number): number {
  let d = ((a2 - a1) % 360 + 360) % 360;
  if (d > 180) d -= 360;
  return d; // (-180,180]
}

export function getGrahaLongitudesAndMotionSidereal(
  { isoDate }: ObserverInput,
  ayanamsaProvider: AyanamsaProvider = constantAyanamsa()
): GrahaMotion {
  const date = new Date(isoDate);
  const time = Astronomy.MakeTime(date);
  const MS_PER_DAY = 86_400_000;
  const timeNext = Astronomy.MakeTime(new Date(date.getTime() + MS_PER_DAY));

  const bodies: [GrahaName, Astronomy.Body][] = [
    ['Sun', Astronomy.Body.Sun],
    ['Moon', Astronomy.Body.Moon],
    ['Mercury', Astronomy.Body.Mercury],
    ['Venus', Astronomy.Body.Venus],
    ['Mars', Astronomy.Body.Mars],
    ['Jupiter', Astronomy.Body.Jupiter],
    ['Saturn', Astronomy.Body.Saturn],
  ];

  const result = {} as GrahaMotion;
  for (const [name, body] of bodies) {
    const trop1 = tropicalEclipticLongitude(body, time);
    const trop2 = tropicalEclipticLongitude(body, timeNext);
    const speed = signedAngleDeltaDeg(trop2, trop1); // deg/day
    const lonSid = toSidereal(trop1, date, ayanamsaProvider);
    result[name] = { lon: lonSid, retrograde: speed < 0, speedDegPerDay: speed };
  }
  return result;
}

/* ---------- Combustion (Asta) ---------- */

export function angularSeparationDeg(a: number, b: number): number {
  let d = ((a - b) % 360 + 360) % 360; // 0..360
  if (d > 180) d = 180 - (d - 180);
  return Math.abs(d);
}

export type CombustionThresholds = {
  Mercury: { direct: number; retro: number };
  Venus: number; Mars: number; Jupiter: number; Saturn: number;
};

export const DEFAULT_COMBUSTION_THRESHOLDS: CombustionThresholds = {
  Mercury: { direct: 14, retro: 12 }, Venus: 10, Mars: 17, Jupiter: 11, Saturn: 15,
};

export type CombustionInfo = { combust: boolean; sepDeg: number; thresholdDeg: number; };
export type CombustionMap = {
  Mercury: CombustionInfo; Venus: CombustionInfo; Mars: CombustionInfo; Jupiter: CombustionInfo; Saturn: CombustionInfo;
};

export function getCombustionStatusSidereal(
  input: ObserverInput,
  ayanamsaProvider: AyanamsaProvider = constantAyanamsa(),
  thresholds: CombustionThresholds = DEFAULT_COMBUSTION_THRESHOLDS
): CombustionMap {
  const mot = getGrahaLongitudesAndMotionSidereal(input, ayanamsaProvider);
  const sunLon = mot.Sun.lon;
  const mercuryThresh = mot.Mercury.retrograde ? thresholds.Mercury.retro : thresholds.Mercury.direct;

  const build = (planet: keyof CombustionMap, thresh: number): CombustionInfo => {
    const lon = mot[planet].lon;
    const sep = angularSeparationDeg(lon, sunLon);
    return { combust: sep <= thresh, sepDeg: sep, thresholdDeg: thresh };
  };

  return {
    Mercury: build('Mercury', mercuryThresh),
    Venus:   build('Venus', thresholds.Venus),
    Mars:    build('Mars', thresholds.Mars),
    Jupiter: build('Jupiter', thresholds.Jupiter),
    Saturn:  build('Saturn', thresholds.Saturn),
  };
}

/* ---------- Nodes (mean) ---------- */

function centuriesSinceJ2000(date: Date): number {
  return (julianDay(date) - 2451545.0) / 36525.0;
}

function meanAscendingNodeLongitudeTropical(date: Date): number {
  const T = centuriesSinceJ2000(date);
  const deg = 125.04455501
    - 1934.1361849 * T
    + 0.0020762    * T*T
    + (T*T*T) / 467410
    - (T*T*T*T) / 606160;
  return norm360(deg);
}

export type NodeLongitudes = { Rahu: number; Ketu: number };

export function getRahuKetuSidereal(
  { isoDate }: ObserverInput,
  ayanamsaProvider: AyanamsaProvider = lahiriAyanamsaApprox
): NodeLongitudes {
  const date = new Date(isoDate);
  const omegaTrop = meanAscendingNodeLongitudeTropical(date);
  const ayan = ayanamsaProvider(date);
  const rahu = norm360(omegaTrop - ayan);
  const ketu = norm360(rahu + 180.0);
  return { Rahu: rahu, Ketu: ketu };
}

/* ---------- Houses, placements, aspects ---------- */

export type PlanetName =
  | 'Sun' | 'Moon' | 'Mercury' | 'Venus' | 'Mars' | 'Jupiter' | 'Saturn'
  | 'Rahu' | 'Ketu';

export function getHouseIndexForLongitude(
  cusps: number[],
  lonDeg: number
): number {
  const lon = norm360(lonDeg);
  for (let i = 0; i < 12; i++) {
    const start = cusps[i];
    const end = norm360(start + 30);
    if (start < end) {
      if (lon >= start && lon < end) return i + 1;
    } else {
      if (lon >= start || lon < end) return i + 1; // wrapped interval
    }
  }
  return 12;
}

export function degreesIntoHouse(
  cusps: number[],
  house1to12: number,
  lonDeg: number
): number {
  const start = cusps[house1to12 - 1];
  return (norm360(lonDeg - start) % 30);
}

export type HousePlacement = { house: number; offsetDeg: number; sign: number };
export type PlanetsInHouses = Record<PlanetName, HousePlacement>;

export function assignPlanetsToHouses(
  cusps: number[],
  longs: Partial<Record<PlanetName, number>>
): PlanetsInHouses {
  const out: Partial<PlanetsInHouses> = {};
  for (const key of Object.keys(longs) as PlanetName[]) {
    const lon = longs[key]!;
    const house = getHouseIndexForLongitude(cusps, lon);
    const offsetDeg = degreesIntoHouse(cusps, house, lon);
    const sign = Math.floor(lon / 30) + 1; // Calculate sign (1-12) from longitude
    if (key === 'Moon') {
      console.log('[assignPlanetsToHouses] Moon longitude:', lon, 'calculated sign:', sign, 'house:', house);
    }
    out[key] = { house, offsetDeg, sign };
  }
  return out as PlanetsInHouses;
}

/** Vedic drishti (house-based) */
export type AspectKind = '7th' | '4th' | '8th' | '5th' | '9th' | '3rd' | '10th';

export interface AspectRow {
  from: PlanetName;
  to: PlanetName;
  kind: AspectKind;
  fromHouse: number; // 1..12
  toHouse: number;   // 1..12
}

function wrap12(n: number) { return ((n % 12) + 12) % 12; } // 0..11
function to1based(zeroIdx: number) { return zeroIdx + 1; }  // 1..12
function advanceSign(sign: number, delta: number) {
  const zero = wrap12((sign - 1) + delta);
  return to1based(zero);
}

function houseIndexFromCusps(cusps: number[], lon: number): number {
  // Whole-sign: H1 spans cusp1..cusp2 (half-open)
  const L = norm360(lon);
  for (let i = 0; i < 12; i++) {
    const a = norm360(cusps[i]);
    const b = norm360(cusps[(i + 1) % 12]);
    const inSpan = a < b ? (L >= a && L < b) : (L >= a || L < b);
    if (inSpan) return i + 1; // 1..12
  }
  return 1;
}

/** House-based Parāśari aspects for planets (optionally includes nodes casting 5/7/9). */
export function getAspects(
  cusps: number[],
  planetLongs: Partial<Record<PlanetName, number>>,
  opts?: { mode?: 'house'; includeNodes?: boolean; nodesUseSeven?: boolean }
): Array<AspectRow & { mode: 'house' }> {
  const includeNodes = !!opts?.includeNodes;
  const nodesUseSeven = opts?.nodesUseSeven !== false; // default true

  const planets: PlanetName[] = [
    'Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn',
    ...(includeNodes ? (['Rahu','Ketu'] as PlanetName[]) : [])
  ];

  // Precompute houses (1..12)
  const houseOf: Record<PlanetName, number> = {} as any;
  for (const p of planets) {
    const lon = planetLongs[p];
    if (typeof lon !== 'number') continue;
    houseOf[p] = houseIndexFromCusps(cusps, lon); // 1..12
  }

  // Helper: add a 0-based offset then map back to 1..12
  const addHouse = (h1based: number, add0based: number) =>
    to1based(wrap12((h1based - 1) + add0based));

  const rows: Array<AspectRow & { mode: 'house' }> = [];

  for (const from of planets) {
    const fromH = houseOf[from];
    if (!fromH) continue;

    // Everyone: 7th (adder +6)
    const adders: Array<{k: AspectKind, a: number}> = [{ k: '7th', a: 6 }];

    // Specials by graha
    if (from === 'Mars') {
      adders.push({ k: '4th', a: 3 }, { k: '8th', a: 7 });
    } else if (from === 'Jupiter') {
      adders.push({ k: '5th', a: 4 }, { k: '9th', a: 8 });
    } else if (from === 'Saturn') {
      adders.push({ k: '3rd', a: 2 }, { k: '10th', a: 9 });
    } else if ((from === 'Rahu' || from === 'Ketu') && !nodesUseSeven) {
      adders.length = 0; // nodes have no aspects in this setting
    }

    for (const {k, a} of adders) {
      const toH = addHouse(fromH, a);
      for (const to of planets) {
        if (to === from) continue;
        if (houseOf[to] === toH) {
          rows.push({ from, to, kind: k, fromHouse: fromH, toHouse: toH, mode: 'house' });
        }
      }
    }
  }
  return rows;
}

/* ===================== Shadbala (virūpa → ratio → percentages) ===================== */

export interface ShadbalaComponents {
  sthana: number;    // 0..100 (for UI display only)
  dig: number;       // 0..100
  kala: number;      // 0..100
  cheshta: number;   // 0..100
  naisargika: number;// 0..100
  drik: number;      // 0..100
}
export interface ShadbalaResult {
  totalPct: number; // derived % (for 7 grahas: ratio×100; for nodes: virūpa/780×100)
  components: ShadbalaComponents;
}
export type ShadbalaReport = Record<PlanetName, ShadbalaResult>;
export type CoreGrahaName = Exclude<PlanetName, 'Rahu'|'Ketu'>;

export interface ShadbalaInput {
  longitudes: Record<PlanetName, number>;
  placements?: Record<PlanetName, number>; // house 1..12 (used by Dig)
  cusps?: number[];                         // fallback to derive house
  retroMap: Partial<Record<PlanetName, boolean>>;
  isDaytime: boolean;
  moonIllumFrac?: number;                   // 0..1 (optional)
}

/* ---- virūpa constants ---- */
const SB_MAX_VIRUPA = { sthana:480, dig:60, kala:60, cheshta:60, naisargika:60, drik:60 } as const;
const SB_MAX_TOTAL_VIRUPA = 780; // sum of the above

const SB_MIN_REQUIRED: Record<CoreGrahaName, number> = {
  Sun:300, Moon:360, Mars:300, Mercury:420, Jupiter:390, Venus:330, Saturn:300
};

/* ---- PLANET LIST FOR SHADBALA ---- */
const SB_PLANETS: PlanetName[] = ['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'];

/* ---- helper tables (own/friends/etc.) reused by Sthāna/Dṛk) ---- */
export const SIGN_LORDS: PlanetName[] = ['Mars','Venus','Mercury','Moon','Sun','Mercury','Venus','Mars','Jupiter','Saturn','Saturn','Jupiter'];
export const OWN_SIGNS: Partial<Record<PlanetName, number[]>> = {
  Sun:[5], Moon:[4],
  Mars:[1,8], Mercury:[3,6],
  Jupiter:[9,12], Venus:[2,7],
  Saturn:[10,11]
};
export const FRIENDS: Partial<Record<PlanetName, PlanetName[]>> = {
  Sun:['Moon','Mars','Jupiter'],
  Moon:['Sun','Mercury'],
  Mars:['Sun','Moon','Jupiter'],
  Mercury:['Sun','Venus'],
  Jupiter:['Sun','Moon','Mars'],
  Venus:['Mercury','Saturn'],
  Saturn:['Mercury','Venus']
};
export const ENEMIES: Partial<Record<PlanetName, PlanetName[]>> = {
  Sun:['Venus','Saturn'],
  Moon:[],
  Mars:['Mercury'],
  Mercury:['Moon'],
  Jupiter:['Venus','Mercury'],
  Venus:['Sun','Moon'],
  Saturn:['Sun','Moon','Mars']
};
export const EXALT: Partial<Record<PlanetName, number>> = {
  Sun: 0*30 + 10, Moon: 1*30 + 3, Mars: 9*30 + 28, Mercury: 5*30 + 15,
  Jupiter: 3*30 + 5, Venus: 11*30 + 27, Saturn: 6*30 + 20
};
export const DEBIL: Partial<Record<PlanetName, number>> = {
  Sun: 6*30 + 10, Moon: 7*30 + 3, Mars: 3*30 + 28, Mercury: 11*30 + 15,
  Jupiter: 9*30 + 5, Venus: 5*30 + 27, Saturn: 0*30 + 20
};

/**
 * Moolatrikona signs and degree ranges for planets
 * Format: [sign (0-11), start_degree, end_degree]
 */
export const MOOLATRIKONA: Partial<Record<PlanetName, [number, number, number]>> = {
  Sun: [4, 0, 20],        // Leo 0-20°
  Moon: [3, 3, 30],       // Cancer 3-30°
  Mars: [0, 0, 12],       // Aries 0-12°
  Mercury: [5, 15, 20],   // Virgo 15-20°
  Jupiter: [8, 0, 10],    // Sagittarius 0-10°
  Venus: [6, 0, 15],      // Libra 0-15°
  Saturn: [9, 0, 20],     // Capricorn 0-20°
};

/**
 * Get planet dignity status (exaltation, debilitation, own sign, moolatrikona, friend, enemy, neutral)
 * Used for displaying dignity in charts and assessing planet strength
 */
export function getPlanetDignity(
  planet: PlanetName,
  longitude: number
): 'exalted' | 'debilitated' | 'moolatrikona' | 'own' | 'friend' | 'enemy' | 'neutral' {
  if (planet === 'Rahu' || planet === 'Ketu') return 'neutral'; // Nodes don't have dignity
  
  const sign = Math.floor(norm360(longitude) / 30); // 0..11
  const degInSign = norm360(longitude) % 30;
  
  // Check exaltation (within 10° orb of exact exaltation degree)
  const exaltDeg = EXALT[planet];
  if (exaltDeg !== undefined) {
    const exaltSign = Math.floor(exaltDeg / 30);
    if (sign === exaltSign) {
      const exactDeg = exaltDeg % 30;
      if (Math.abs(degInSign - exactDeg) <= 10) {
        return 'exalted';
      }
    }
  }
  
  // Check debilitation (within 10° orb of exact debilitation degree)
  const debilDeg = DEBIL[planet];
  if (debilDeg !== undefined) {
    const debilSign = Math.floor(debilDeg / 30);
    if (sign === debilSign) {
      const exactDeg = debilDeg % 30;
      if (Math.abs(degInSign - exactDeg) <= 10) {
        return 'debilitated';
      }
    }
  }
  
  // Check moolatrikona (exact sign and degree range)
  const moolatrikona = MOOLATRIKONA[planet];
  if (moolatrikona) {
    const [mtSign, startDeg, endDeg] = moolatrikona;
    if (sign === mtSign && degInSign >= startDeg && degInSign <= endDeg) {
      return 'moolatrikona';
    }
  }
  
  // Check own sign
  const ownSigns = OWN_SIGNS[planet];
  if (ownSigns?.includes(sign)) {
    return 'own';
  }
  
  // Check friend/enemy sign (based on sign lord)
  const signLord = SIGN_LORDS[sign];
  const friends = FRIENDS[planet];
  const enemies = ENEMIES[planet];
  
  if (friends?.includes(signLord)) {
    return 'friend';
  }
  
  if (enemies?.includes(signLord)) {
    return 'enemy';
  }
  
  return 'neutral';
}

/**
 * Classical Vedic rule: Conjunction-based exaltation/debilitation
 * 
 * If a planet is conjunct (within 10°, same house) with the lord of its 
 * exaltation/debilitation sign, the planet behaves as if exalted/debilitated.
 * 
 * Example: Moon's debilitation sign is Scorpio (ruled by Mars). If Moon is conjunct 
 * with Mars in ANY sign, Moon is considered functionally debilitated via conjunction.
 * 
 * This is part of Neecha Bhanga Raja Yoga and exaltation enhancement principles.
 */
export function getEnhancedExaltDebil(
  placements: Record<PlanetName, { house: number }>,
  longitudes: Record<PlanetName, number>
): {
  exaltEnhanced: Partial<Record<PlanetName, boolean>>;
  debilEnhanced: Partial<Record<PlanetName, boolean>>;
} {
  const exaltEnhanced: Partial<Record<PlanetName, boolean>> = {};
  const debilEnhanced: Partial<Record<PlanetName, boolean>> = {};

  // Exaltation sign lords
  const EXALT_SIGN_LORDS: Partial<Record<PlanetName, PlanetName>> = {
    Sun: 'Mars',      // Sun exalts in Aries (ruled by Mars)
    Moon: 'Venus',    // Moon exalts in Taurus (ruled by Venus)
    Mars: 'Saturn',   // Mars exalts in Capricorn (ruled by Saturn)
    Mercury: 'Venus', // Mercury exalts in Virgo (ruled by Venus)
    Jupiter: 'Moon',  // Jupiter exalts in Cancer (ruled by Moon)
    Venus: 'Jupiter', // Venus exalts in Pisces (ruled by Jupiter)
    Saturn: 'Venus'   // Saturn exalts in Libra (ruled by Venus)
  };

  // Debilitation sign lords
  const DEBIL_SIGN_LORDS: Partial<Record<PlanetName, PlanetName>> = {
    Sun: 'Venus',     // Sun debilitates in Libra (ruled by Venus)
    Moon: 'Mars',     // Moon debilitates in Scorpio (ruled by Mars)
    Mars: 'Moon',     // Mars debilitates in Cancer (ruled by Moon)
    Mercury: 'Jupiter', // Mercury debilitates in Pisces (ruled by Jupiter)
    Jupiter: 'Saturn',  // Jupiter debilitates in Capricorn (ruled by Saturn)
    Venus: 'Venus',     // Venus debilitates in Virgo (ruled by Venus - self-ruled)
    Saturn: 'Mars'      // Saturn debilitates in Aries (ruled by Mars)
  };

  const CONJUNCTION_ORB = 10; // degrees for conjunction

  const sevenPlanets: PlanetName[] = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn'];

  for (const planet of sevenPlanets) {
    const exaltLord = EXALT_SIGN_LORDS[planet];
    const debilLord = DEBIL_SIGN_LORDS[planet];
    
    if (!exaltLord || !debilLord) continue;

    const planetLon = longitudes[planet];
    const planetHouse = placements[planet]?.house;
    
    if (!planetLon || !planetHouse) continue;

    // Check exaltation enhancement: planet conjunct its exaltation sign lord
    if (exaltLord !== planet) { // Skip if self-ruled
      const lordLon = longitudes[exaltLord];
      const lordHouse = placements[exaltLord]?.house;
      
      if (lordLon !== undefined && lordHouse === planetHouse) {
        const separation = angularSeparationDeg(planetLon, lordLon);
        if (separation <= CONJUNCTION_ORB) {
          exaltEnhanced[planet] = true;
        }
      }
    }

    // Check debilitation enhancement: planet conjunct its debilitation sign lord
    if (debilLord !== planet) { // Skip if self-ruled
      const lordLon = longitudes[debilLord];
      const lordHouse = placements[debilLord]?.house;
      
      if (lordLon !== undefined && lordHouse === planetHouse) {
        const separation = angularSeparationDeg(planetLon, lordLon);
        if (separation <= CONJUNCTION_ORB) {
          debilEnhanced[planet] = true;
        }
      }
    }
  }

  return { exaltEnhanced, debilEnhanced };
}

const NAISARGIKA_VIRUPA: Record<CoreGrahaName, number> = {
  Sun: 60.00, Moon: 51.43, Mars: 17.14, Mercury: 25.71, Jupiter: 34.29, Venus: 42.86, Saturn: 8.57
};
const BENEFICS: PlanetName[] = ['Jupiter','Venus','Mercury','Moon'];
const MALEFICS: PlanetName[] = ['Saturn','Mars','Sun','Rahu','Ketu'];

/* ---- misc helpers ---- */
const sbDiffDeg = (a:number,b:number) => {
  let d = Math.abs(norm360(a) - norm360(b));
  return d > 180 ? 360 - d : d;
};
const signOf = (lon:number) => Math.floor(norm360(lon)/30); // 0..11

/* ---------- “Classical-lite” component calculators ---------- */

// Sthāna → % (UI) then mapped to virūpa (max 480)
function sthanaPct(planet: PlanetName, lon: number): number {
  if (planet === 'Rahu' || planet === 'Ketu') return 50;
  const s = signOf(lon); // 0..11
  const lord = SIGN_LORDS[s];
  const own = OWN_SIGNS[planet]?.includes(s);
  const friend = FRIENDS[planet]?.includes(lord);
  const enemy = ENEMIES[planet]?.includes(lord);

  let base = 60;
  if (own) base = 80; else if (friend) base = 70; else if (enemy) base = 50;

  const ex = EXALT[planet], de = DEBIL[planet];
  const exBoost = ex!=null ? Math.max(0, 1 - sbDiffDeg(lon, ex)/30)*20 : 0;
  const dePen   = de!=null ? Math.max(0, 1 - sbDiffDeg(lon, de)/30)*25 : 0;

  const pct = Math.max(0, Math.min(100, base + exBoost - dePen));
  return pct;
}

// Dig → % via soft house-distance, then virūpa (max 60)
function digPct(planet: PlanetName, house: number): number {
  const ideal: Partial<Record<PlanetName, number>> = {
    Jupiter:1, Mercury:1, Sun:10, Mars:10, Saturn:7, Moon:4, Venus:4, Rahu:7, Ketu:1
  };
  const best = ideal[planet] ?? 1;
  const d = Math.min(Math.abs(house-best), 12-Math.abs(house-best));
  const x = (d/6)*Math.PI;        // 0..π
  return Math.round(Math.max(0, Math.cos(x))*100);
}

// Kāla → % using day/night + Moon illumination (virūpa max 60)
function kalaPct(planet: PlanetName, isDaytime: boolean, moonIllum?: number): number {
  let base = 60;
  if (planet === 'Mercury') base = 70;
  else if (['Sun','Jupiter','Saturn'].includes(planet)) base = isDaytime ? 85 : 55;
  else if (['Moon','Mars','Venus'].includes(planet))    base = isDaytime ? 55 : 85;
  else base = 60; // nodes

  if (planet === 'Moon' && typeof moonIllum === 'number') {
    base += (moonIllum - 0.5) * 30; // -15..+15
  }
  return Math.max(0, Math.min(100, base));
}

// Cheṣṭā → % from retro status (UI table only - simplified)
function cheshtaPct(planet: PlanetName, retro?: boolean): number {
  if (planet === 'Sun' || planet === 'Moon') return 60;
  return retro ? 90 : 65;
}

/** 
 * Cheshta Bala (0-60) - Hybrid BPHS implementation
 * 
 * Uses astronomy-engine to calculate actual daily motion, then applies
 * classical BPHS formulas. This provides good accuracy without requiring
 * Swiss Ephemeris native compilation.
 * 
 * Accuracy: ±5-10% (better than simple elongation formulas)
 * 
 * For 100% accuracy, deploy Swiss Ephemeris service (see docs/CLOUD_SWISSEPH_COMPLETE.md)
 */
function computeProperChestaBala(
  planet: PlanetName,
  date: Date,
  currentLon: number,
  retrograde: boolean,
  sunLon: number,
  moonLon?: number
): number {
  if (planet === 'Rahu' || planet === 'Ketu') return 30;

  const norm360 = (x: number) => ((x % 360) + 360) % 360;
  const sep180 = (a: number, b: number) => {
    let d = Math.abs(norm360(a) - norm360(b));
    return d > 180 ? 360 - d : d;
  };

  // Sun: Based on declination (seasonal variation)
  if (planet === 'Sun') {
    const t = Astronomy.MakeTime(date);
    const tropSun = tropicalEclipticLongitude(Astronomy.Body.Sun, t);
    const lonFrom90 = norm360(tropSun + 90);
    const k = lonFrom90 > 180 ? 360 - lonFrom90 : lonFrom90;
    return k / 3;
  }

  // Moon: Based on elongation (paksha strength)
  if (planet === 'Moon' && moonLon !== undefined) {
    const elong = sep180(moonLon, sunLon);
    return elong / 3;
  }

  // For other planets, use ACTUAL daily motion from astronomy-engine
  const bodyMap: Partial<Record<PlanetName, Astronomy.Body>> = {
    Mercury: Astronomy.Body.Mercury,
    Venus: Astronomy.Body.Venus,
    Mars: Astronomy.Body.Mars,
    Jupiter: Astronomy.Body.Jupiter,
    Saturn: Astronomy.Body.Saturn,
  };

  const body = bodyMap[planet];
  if (!body) return 30;

  // Calculate actual daily motion by sampling positions
  const t0 = Astronomy.MakeTime(new Date(date.getTime() - 12 * 3600 * 1000));
  const t1 = Astronomy.MakeTime(new Date(date.getTime() + 12 * 3600 * 1000));
  
  const lon0 = tropicalEclipticLongitude(body, t0);
  const lon1 = tropicalEclipticLongitude(body, t1);
  
  let dailyMotion = lon1 - lon0;
  if (dailyMotion < -180) dailyMotion += 360;
  if (dailyMotion > 180) dailyMotion -= 360;

  const actualSpeed = Math.abs(dailyMotion); // degrees per day

  // Mean daily motions (classical values)
  const meanMotions: Record<string, number> = {
    Mercury: 1.3833,
    Venus: 1.6021,
    Mars: 0.5240,
    Jupiter: 0.0831,
    Saturn: 0.0335,
  };

  const meanMotion = meanMotions[planet] || 1;

  // BPHS Cheshta Bala formula based on speed variation
  let chesta: number;

  if (dailyMotion < 0) {
    // Retrograde: high strength
    chesta = 60;
  } else if (actualSpeed > meanMotion) {
    // Faster than mean: increasing strength
    const ratio = Math.min(actualSpeed / (meanMotion * 2), 1);
    chesta = 30 + (ratio * 30); // 30-60 range
  } else {
    // Slower than mean: decreasing strength
    const ratio = actualSpeed / meanMotion;
    chesta = ratio * 30; // 0-30 range
  }

  return Math.max(0, Math.min(60, chesta));
}

// Naiṣargika: fixed virūpa (map to % only for UI)
function naisargikaVirupa(planet: PlanetName): number {
  if (planet === 'Rahu' || planet === 'Ketu') return 45;
  return NAISARGIKA_VIRUPA[planet as CoreGrahaName] ?? 60;
}

// Dṛk → % from benefic/malefic aspects
function drikPct(target: PlanetName, targetLon: number, longitudes: Record<PlanetName, number>): number {
  const seventh = (sep:number) => Math.max(0, 1 - Math.min(1, Math.abs(sep-180)/12));
  const special = (from:PlanetName, sep:number) => {
    const orb=10, near=(t:number)=>Math.max(0,1-Math.min(1,Math.abs(sep-t)/orb));
    if (from==='Mars')    return Math.max(near(90), near(150));
    if (from==='Jupiter') return Math.max(near(60), near(120));
    if (from==='Saturn')  return Math.max(near(60), near(120));
    return 0;
  };

  let score = 0;
  for (const p of SB_PLANETS) {
    if (p === target) continue;
    const lon = longitudes[p]; if (typeof lon !== 'number') continue;
    const sep = sbDiffDeg(lon, targetLon);
    const s = Math.max(seventh(sep), special(p, sep));
    if (s <= 0) continue;
    const mag = s * 20;
    if (BENEFICS.includes(p)) score += mag;
    if (MALEFICS.includes(p)) score -= mag;
  }
  return Math.max(0, Math.min(100, 60 + score));
}

/* ---- Public: virūpa-first computation ---- */

export type ShadbalaVirupaComponents = {
  sthana:number; dig:number; kala:number; cheshta:number; naisargika:number; drik:number;
};
export type ShadbalaVirupaRow = {
  components: ShadbalaVirupaComponents;
  total: number;                 // total virūpa (0..780)
  minRequired?: number;          // set for 7 classical planets
  ratio?: number;                // total/min (for 7 classical planets)
};

function sbHouseFromCusps(lon:number, cusps:number[]):number{
  const L=norm360(lon);
  for(let i=0;i<12;i++){
    const a=norm360(cusps[i]), b=norm360(cusps[(i+1)%12]);
    const inSpan = a<b ? (L>=a && L<b) : (L>=a || L<b);
    if(inSpan) return i+1;
  }
  return 1;
}

export function computeShadbalaVirupa(
  input: ShadbalaInput,
  opts?: { 
    chartDate?: Date; 
    vargas?: ReturnType<typeof computeImplementedVargas>;
    fiveFoldFriendships?: import('./fiveFoldFriendship').PlanetFriendship[];
  }
): Record<PlanetName, ShadbalaVirupaRow> {
  const { longitudes, placements, cusps, retroMap, isDaytime, moonIllumFrac } = input;
  const chartDate = opts?.chartDate ?? new Date();
  const vargas = opts?.vargas;
  const fiveFoldFriendships = opts?.fiveFoldFriendships;

  const houseOf = (pl:PlanetName, lon:number) =>
     placements?.[pl] ?? (cusps && cusps.length===12 ? sbHouseFromCusps(lon, cusps) : 1);

  const out = {} as Record<PlanetName, ShadbalaVirupaRow>;

  // Weekday for Vara Bala
  const weekday = chartDate.getUTCDay();
  
  // Sun longitude for Ayana Bala
  const sunLon = longitudes.Sun ?? 0;

  for (const pl of SB_PLANETS) {
    const lon = longitudes[pl]; if (typeof lon!=='number') continue;
    const h = houseOf(pl, lon);
    const retro = !!retroMap[pl];

    // ========== CLASSICAL STHAN BALA (max 238 virupa) ==========
    // Sub-components: Saptavargaja(133) + Ojayugma(30) + Kendra(60) + Drekkana(15) = 238
    const v_saptavargaja = saptavargajaBala(pl, lon, vargas, fiveFoldFriendships);
    const v_ojayugma = ojayugmarasyamsaBala(pl, lon);
    const v_kendra = kendraBala(h);
    const v_drekkana = drekkanaBala(pl, lon);
    const v_sthanaBala = v_saptavargaja + v_ojayugma + v_kendra + v_drekkana;
    
    // Scale to our max 480 for UI compatibility (238 classical → 480 UI)
    const v_sthana_scaled = (v_sthanaBala / 238) * SB_MAX_VIRUPA.sthana;

    // ========== DIG BALA (max 60 virupa) ==========
    const c_dig = digPct(pl, h);
    const v_dig = (c_dig * SB_MAX_VIRUPA.dig) / 100;

    // ========== CLASSICAL KALA BALA (multiple sub-components) ==========
    // Base components we already have
    const c_kala_base = kalaPct(pl, isDaytime, moonIllumFrac); // Nathonnatha + Paksha
    const v_kala_base = (c_kala_base * 60) / 100; // Scale to ~60 virupa
    
    // Additional classical components
    const v_vara = varaBala(pl, weekday);
    const v_hora = horaBala(pl, chartDate, 0, 0); // Simplified (lat/lon not used in simplified version)
    const v_ayana = ayanaBala(pl, sunLon);
    
    // Total Kala Bala (can exceed 60 with all sub-components)
    const v_kalaBala = v_kala_base + v_vara + v_hora + v_ayana;
    
    // Scale to our max 60 for UI compatibility
    const v_kala_scaled = Math.min(v_kalaBala, SB_MAX_VIRUPA.kala);

    // ========== CHESHTA BALA (max 60 virupa) ==========
    const cheshtaBala60 = computeProperChestaBala(pl, chartDate, lon, retro, longitudes.Sun!, longitudes.Moon);
    const v_cheshta = cheshtaBala60;

    // ========== NAISARGIKA BALA (max 60 virupa) ==========
    const v_naisargika = naisargikaVirupa(pl);

    // ========== DRIK BALA (max 60 virupa, can be negative) ==========
    const c_drik = drikPct(pl, lon, longitudes);
    const v_drik = (c_drik * SB_MAX_VIRUPA.drik) / 100;

    // Store components
    const cv: ShadbalaVirupaComponents = {
      sthana:     v_sthana_scaled,
      dig:        v_dig,
      kala:       v_kala_scaled,
      cheshta:    v_cheshta,
      naisargika: v_naisargika,
      drik:       v_drik,
    };

    const total = Math.round((cv.sthana+cv.dig+cv.kala+cv.cheshta+cv.naisargika+cv.drik)*10)/10;

    if ((['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn'] as PlanetName[]).includes(pl)) {
      const key = pl as CoreGrahaName;
      const minRequired = SB_MIN_REQUIRED[key];
      const ratio = Math.round((total/minRequired)*100)/100;
      out[pl] = { components: cv, total, minRequired, ratio };
    } else {
      out[pl] = { components: cv, total };
    }
  }
  return out;
}

/* ---- ratios (7 grahas) from virūpa ---- */
export type ShadbalaRatioRow = { virupa:number; minRequired:number; ratio:number };

export function estimateShadbalaRatios(
  input: ShadbalaInput,
  opts?: { 
    chartDate?: Date; 
    vargas?: ReturnType<typeof computeImplementedVargas>;
    fiveFoldFriendships?: import('./fiveFoldFriendship').PlanetFriendship[];
  }
): Record<CoreGrahaName, ShadbalaRatioRow> {
  const vr = computeShadbalaVirupa(input, opts);
  const out = {} as Record<CoreGrahaName, ShadbalaRatioRow>;

  (['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn'] as CoreGrahaName[]).forEach(pl => {
    const row = vr[pl]!;
    out[pl] = {
      virupa: row.total,
      minRequired: row.minRequired!,
      ratio: row.ratio!
    };
  });
  return out;
}

/* ---- final UI percentages derived from virūpa/ratio ---- */
export function computeShadbalaPercentages(
  input: ShadbalaInput, 
  opts?: { 
    chartDate?: Date; 
    vargas?: ReturnType<typeof computeImplementedVargas>;
    fiveFoldFriendships?: import('./fiveFoldFriendship').PlanetFriendship[];
  }
): ShadbalaReport {
  const vr = computeShadbalaVirupa(input, opts);
  const out = {} as ShadbalaReport;

  for (const pl of SB_PLANETS) {
    const row = vr[pl]; if (!row) continue;

    const components: ShadbalaComponents = {
      sthana:     Math.round((row.components.sthana     / SB_MAX_VIRUPA.sthana    )*100),
      dig:        Math.round((row.components.dig        / SB_MAX_VIRUPA.dig       )*100),
      kala:       Math.round((row.components.kala       / SB_MAX_VIRUPA.kala      )*100),
      cheshta:    Math.round((row.components.cheshta    / SB_MAX_VIRUPA.cheshta   )*100),
      naisargika: Math.round((row.components.naisargika / SB_MAX_VIRUPA.naisargika)*100),
      drik:       Math.round((row.components.drik       / SB_MAX_VIRUPA.drik      )*100),
    };

    let totalPct: number;
    if (row.minRequired && row.ratio != null) {
      totalPct = Math.round(row.ratio * 1000) / 10;
    } else {
      totalPct = Math.round((row.total / SB_MAX_TOTAL_VIRUPA) * 1000) / 10;
    }

    out[pl] = { totalPct, components };
  }

  return out;
}

/* ===================== Iṣṭa / Kaṣṭa (Maitreya-style cheṣṭā) ===================== */

export type IshtaKashtaRow = {
  ucchaBala: number;      // 0..60
  cheshtaBala: number;    // 0..60
  ishta: number;          // 0..60
  kashta: number;         // 0..60
  ishtaPct: number;       // 0..100
  kashtaPct: number;      // 0..100
};

export function computeIshtaKashtaPhala(
  input: ShadbalaInput,
  opts?: { isoDate?: string; formula?: 'geometric' | 'arithmetic'; shadbalaVirupa?: Record<PlanetName, ShadbalaVirupaRow> }
): Record<Exclude<PlanetName, 'Rahu'|'Ketu'>, IshtaKashtaRow> {
  const { longitudes } = input;
  const formula = opts?.formula ?? 'geometric'; // Default to geometric (BPHS classical formula)
  const useProperChesta = !!opts?.shadbalaVirupa; // Use full Shadbala Cheshta if available

  // helpers
  const norm = (x:number)=>((x%360)+360)%360;
  const sep180 = (a:number,b:number)=>{ let d=Math.abs(norm(a)-norm(b)); return d>180?360-d:d; };
  const round1 = (x:number)=>Math.round(x*10)/10;

  // Use chart datetime if provided (for Sun’s cheṣṭā)
  const chartDate = opts?.isoDate ? new Date(opts.isoDate) : new Date();
  const tChart = Astronomy.MakeTime(chartDate);
  const sunSayana = tropicalEclipticLongitude(Astronomy.Body.Sun, tChart);

  // Uccha Bala (sidereal, 0..60)
  const ucchaBalaFor = (pl: Exclude<PlanetName,'Rahu'|'Ketu'>): number => {
    const lon = longitudes[pl]!;
    const ex  = EXALT[pl]!;
    const dToEx = sep180(lon, ex);        // 0..180
    const ub = ((180 - dToEx) * 60) / 180; // 60 at exaltation, 0 at debility
    return Math.max(0, Math.min(60, ub));
  };

  // Cheṣṭā Bala for Ishta/Kashta - Classical elongation-based formula
  // This differs from Shadbala Cheshta which uses daily motion.
  // Interior planets (Mercury/Venus) use scaled elongation based on max elongation.
  // Outer planets and luminaries use different formulas.
  
  // Classical mean daily motions (degrees per day) - BPHS values
  const MEAN_MOTIONS = {
    Sun: 0.9856,      // ~1° per day
    Moon: 13.1764,    // ~13° per day
    Mars: 0.5240,     // ~0.52° per day
    Mercury: 1.3833,  // ~1.38° per day (varies widely)
    Jupiter: 0.0831,  // ~0.08° per day
    Venus: 1.6021,    // ~1.60° per day
    Saturn: 0.0335    // ~0.03° per day
  };

  const chestaForIshtaKashta = (pl: Exclude<PlanetName,'Rahu'|'Ketu'>): number => {
    if (pl === 'Sun') {
      // Sun: based on declination (distance from equator)
      let k = norm(sunSayana + 90);
      if (k > 180) k = 360 - k;
      return k / 3; // 0..60
    }
    
    if (pl === 'Moon') {
      // Moon: elongation from Sun / 3 (Paksha strength)
      return sep180(longitudes.Moon!, longitudes.Sun!) / 3;
    }
    
    // For Mars, Mercury, Jupiter, Venus, Saturn: use motion-based Cheshta (BPHS)
    // Calculate actual daily speed and compare with mean motion
    
    // Get actual speed from astronomy-engine
    const body = Astronomy.Body[pl as 'Mars'|'Mercury'|'Jupiter'|'Venus'|'Saturn'];
    const time = Astronomy.MakeTime(chartDate);
    
    // Calculate speed: difference in tropical longitude over 1 day
    const lon1 = tropicalEclipticLongitude(body, time);
    
    // One day later
    const date2 = new Date(chartDate.getTime() + 86400000); // +1 day in ms
    const time2 = Astronomy.MakeTime(date2);
    const lon2 = tropicalEclipticLongitude(body, time2);
    
    let actualSpeed = lon2 - lon1;
    // Handle wrap-around (e.g., 359° to 1°)
    if (actualSpeed > 180) actualSpeed -= 360;
    if (actualSpeed < -180) actualSpeed += 360;
    
    const meanSpeed = MEAN_MOTIONS[pl];
    const isRetrograde = actualSpeed < 0;
    const absSpeed = Math.abs(actualSpeed);
    const speedRatio = absSpeed / meanSpeed;
    
    // Maitreya/BPHS Cheshta Bala classification (simplified 2-tier system):
    // Based on empirical observation from Maitreya software:
    // - Vakra (strong retrograde, ratio > ~0.7): 60 virupas
    // - All other motion types: 30 virupas
    // 
    // Traditional BPHS mentions: Vakra, Anuvakra, Manda, Sama, Sheeghra, Atichara
    // But practical implementation uses simplified values for Ishta/Kashta
    
    if (isRetrograde && speedRatio > 0.7) {
      // Vakra (strong retrograde): maximum Cheshta
      return 60;
    } else {
      // All other types (Anuvakra, Manda, Sama, Sheeghra, Atichara): baseline
      // This includes weak retrograde, slow, normal, and fast direct motion
      return 30;
    }
  };

  const out = {} as Record<Exclude<PlanetName,'Rahu'|'Ketu'>, IshtaKashtaRow>;

  (['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn'] as const).forEach(pl => {
    const ub = ucchaBalaFor(pl);
    const lon = longitudes[pl]!;
    const retro = !!input.retroMap[pl];

    let cb: number;
    
    // For Ishta/Kashta, use classical elongation-based Cheshta (not motion-based)
    // This matches traditional software like Astro-Sage, JHora, etc.
    cb = chestaForIshtaKashta(pl);

    let ishta: number, kashta: number;
    
    if (formula === 'arithmetic') {
      // Arithmetic mean approach (simpler, matches many modern software)
      ishta  = (ub + cb) / 2;
      kashta = 60 - ishta;
    } else {
      // Geometric mean approach (BPHS classical)
      ishta  = Math.sqrt(Math.max(0, ub) * Math.max(0, cb));
      kashta = Math.sqrt(Math.max(0, 60 - ub) * Math.max(0, 60 - cb));
    }

    out[pl] = {
      ucchaBala:   round1(ub),
      cheshtaBala: round1(cb),
      ishta:       round1(ishta),
      kashta:      round1(kashta),
      ishtaPct:    round1((ishta / 60)  * 100),
      kashtaPct:   round1((kashta / 60) * 100),
    };
  });

  return out;
}

/* === Debug helper: dump IK ingredients per planet === */
export type IkDebugRow = {
  planet: Exclude<PlanetName,'Rahu'|'Ketu'>;
  lon: number;                      // sidereal longitude
  exaltDeg: number;                 // exaltation sidereal longitude
  debilDeg: number;                 // debil sidereal longitude
  distToExalt: number;              // 0..180 (sep)
  distToDebil: number;              // 0..180 (sep)
  ucchaBala: number;                // 0..60
  elongFromSun: number;             // 0..180 (deg)
  cheshtaBala: number;              // 0..60
  ishta: number;                    // 0..60
  kashta: number;                   // 0..60
};

export function debugIshtaKashtaDetails(
  input: ShadbalaInput,
  opts?: { isoDate?: string }
): IkDebugRow[] {
  const { longitudes, retroMap } = input;

  const norm = (x:number)=>((x%360)+360)%360;
  const sep180 = (a:number,b:number)=>{ let d=Math.abs(norm(a)-norm(b)); return d>180?360-d:d; };

  const chartDate = opts?.isoDate ? new Date(opts.isoDate) : new Date();
  const tChart = Astronomy.MakeTime(chartDate);
  const sunSayana = tropicalEclipticLongitude(Astronomy.Body.Sun, tChart);

  const rows: IkDebugRow[] = [];
  (['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn'] as const).forEach(pl => {
    const lon = longitudes[pl]!;
    const ex  = EXALT[pl]!;
    const de  = DEBIL[pl]!;
    const distEx = sep180(lon, ex);
    const distDe = sep180(lon, de);
    const uccha = ((180 - distEx) * 60) / 180;

    // Compute elongation for display
    const elong = sep180(lon, longitudes.Sun!);
    
    // Use classical elongation-based Cheshta for Ishta/Kashta
    let cb: number;
    if (pl === 'Sun') {
      let k = norm(sunSayana + 90);
      if (k > 180) k = 360 - k;
      cb = k / 3;
    } else if (pl === 'Moon') {
      cb = sep180(longitudes.Moon!, longitudes.Sun!) / 3;
    } else if (pl === 'Mercury') {
      // Mercury: scale by max elongation (28°)
      cb = Math.min((elong / 28) * 60, 60);
    } else if (pl === 'Venus') {
      // Venus: scale by max elongation (47°)
      cb = Math.min((elong / 47) * 60, 60);
    } else {
      // Outer planets: elongation / 3
      cb = elong / 3;
    }

    // Use arithmetic mean formula: ishta = (uccha + cb) / 2
    const ishta  = (uccha + cb) / 2;
    const kashta = 60 - ishta;

    rows.push({
      planet: pl, lon, exaltDeg: ex, debilDeg: de,
      distToExalt: Math.round(distEx*100)/100,
      distToDebil: Math.round(distDe*100)/100,
      ucchaBala: Math.round(uccha*10)/10,
      elongFromSun: Math.round(elong*100)/100,
      cheshtaBala: Math.round(cb*10)/10,
      ishta: Math.round(ishta*10)/10,
      kashta: Math.round(kashta*10)/10
    });
  });

  return rows;
}

/* ===================== Vimśottarī Daśā + Functional Nature ===================== */

// Planet set for UI coloring (seven classical)
export type DashaPlanet = 'Sun'|'Moon'|'Mars'|'Mercury'|'Jupiter'|'Venus'|'Saturn';

type DashaBand = {
  start: string; // ISO
  end: string;   // ISO
  lord: DashaPlanet | 'Rahu' | 'Ketu';
  level: 'MD'|'AD'|'PD';
};

export type DashaTimeline = {
  md: DashaBand[];
  ad: DashaBand[];
  pd: DashaBand[];
  range: { start: string; end: string };
};

// Nakshatra → lord
const LORD_FOR_NAK = (nakIndex0to26: number): 'Ketu'|'Venus'|'Sun'|'Moon'|'Mars'|'Rahu'|'Jupiter'|'Saturn'|'Mercury' =>
  (['Ketu','Venus','Sun','Moon','Mars','Rahu','Jupiter','Saturn','Mercury'][nakIndex0to26 % 9] as any);

// Duration (years)
const DASHA_YEARS: Record<'Ketu'|'Venus'|'Sun'|'Moon'|'Mars'|'Rahu'|'Jupiter'|'Saturn'|'Mercury', number> = {
  Ketu:7, Venus:20, Sun:6, Moon:10, Mars:7, Rahu:18, Jupiter:16, Saturn:19, Mercury:17
};

function* cycleFrom(startLord: string) {
  const base = ['Ketu','Venus','Sun','Moon','Mars','Rahu','Jupiter','Saturn','Mercury'];
  const idx = base.indexOf(startLord);
  for (let i=0;i<base.length;i++) yield base[(idx+i)%base.length];
}

function clampISODate(dt: DateTime, start: DateTime, end: DateTime) {
  if (dt < start) return start;
  if (dt > end) return end;
  return dt;
}

function nakIndexFromMoonLon(moonLonSiderealDeg: number) {
  const NAK_SIZE = 13 + 20/60; // 13°20′
  let idx = Math.floor((moonLonSiderealDeg % 360 + 360) % 360 / NAK_SIZE);
  if (idx < 0) idx += 27;
  return idx; // 0..26
}

/**
 * Compute Vimśottarī timeline (MD/AD/PD) for a viewing window.
 * - Uses Moon’s **sidereal** longitude at birth to pick starting Nakshatra & fractional remainder.
 * - Antardaśā/Pratyantardaśā are proportional to parent band by (years(lord)/120).
 * - Returns nodes in sequence but filters UI rows to the seven planets for color-coding convenience.
 */
export function getVimshottariTimeline(args: {
  birthDateTime: DateTime;        // Luxon DateTime with correct zone
  moonLonSiderealDeg: number;     // 0..360
  viewStart: DateTime;
  viewEnd: DateTime;
}): DashaTimeline {
  const { birthDateTime, moonLonSiderealDeg, viewStart, viewEnd } = args;

  const startNak = nakIndexFromMoonLon(moonLonSiderealDeg);
  const startLord = LORD_FOR_NAK(startNak);

  const NAK_SIZE = 13 + 20/60;
  const posInNak = ((moonLonSiderealDeg % 360) + 360) % 360 - startNak*NAK_SIZE;
  const fracRemaining = 1 - (posInNak / NAK_SIZE);

  // --- Mahadasha sequence across 120 years ---
  let cursor = birthDateTime;
  const md: DashaBand[] = [];

  // first partial MD
  {
    const totalYears = DASHA_YEARS[startLord];
    const firstYears = totalYears * fracRemaining;
    const mdStart = cursor;
    const mdEnd = cursor.plus({ years: firstYears });
    md.push({ start: mdStart.toISO()!, end: mdEnd.toISO()!, lord: startLord, level: 'MD' });
    cursor = mdEnd;
  }

  // remaining MDs
  {
    const seq = [...cycleFrom(startLord)];
    seq.shift(); // we already used startLord partially
    const hardEnd = birthDateTime.plus({ years: 120 });
    let guard = 0;
    while (cursor < hardEnd && guard < 1000) {
      guard++;
      const lord = seq[0] as keyof typeof DASHA_YEARS;
      const years = DASHA_YEARS[lord];
      const mdStart = cursor;
      const mdEnd = cursor.plus({ years });
      md.push({ start: mdStart.toISO()!, end: mdEnd.toISO()!, lord, level: 'MD' });
      cursor = mdEnd;
      seq.push(seq.shift()!);
    }
  }

  // clip MD to view
  const mdView = md
    .filter(b => DateTime.fromISO(b.end) > viewStart && DateTime.fromISO(b.start) < viewEnd)
    .map(b => {
      const s = clampISODate(DateTime.fromISO(b.start), viewStart, viewEnd);
      const e = clampISODate(DateTime.fromISO(b.end), viewStart, viewEnd);
      return { ...b, start: s.toISO()!, end: e.toISO()! };
    });

  // --- Antardasha ---
  const ad: DashaBand[] = [];
  for (const m of mdView) {
    const ms = DateTime.fromISO(m.start), me = DateTime.fromISO(m.end);
    const mdDur = me.toMillis() - ms.toMillis();
    const seq = [...cycleFrom(m.lord)];
    let cur = ms, guard = 0;
    while (cur < me && guard < 100) {
      guard++;
      const lord = seq.shift() as keyof typeof DASHA_YEARS;
      seq.push(lord);
      const frac = DASHA_YEARS[lord] / 120;
      const dur = mdDur * frac;
      const s = cur;
      const e = DateTime.fromMillis(cur.toMillis() + dur);
      const band: DashaBand = { start: s.toISO()!, end: e.toISO()!, lord, level: 'AD' };
      if (e > viewStart && s < viewEnd) {
        ad.push({ ...band,
          start: clampISODate(s, viewStart, viewEnd).toISO()!,
          end: clampISODate(e, viewStart, viewEnd).toISO()!
        });
      }
      cur = e;
    }
  }

  // --- Pratyantardasha ---
  const pd: DashaBand[] = [];
  for (const a of ad) {
    const as = DateTime.fromISO(a.start), ae = DateTime.fromISO(a.end);
    const adDur = ae.toMillis() - as.toMillis();
    const seq = [...cycleFrom(a.lord)];
    let cur = as, guard = 0;
    while (cur < ae && guard < 100) {
      guard++;
      const lord = seq.shift() as keyof typeof DASHA_YEARS;
      seq.push(lord);
      const frac = DASHA_YEARS[lord] / 120;
      const dur = adDur * frac;
      const s = cur;
      const e = DateTime.fromMillis(cur.toMillis() + dur);
      const band: DashaBand = { start: s.toISO()!, end: e.toISO()!, lord, level: 'PD' };
      if (e > viewStart && s < viewEnd) {
        pd.push({ ...band,
          start: clampISODate(s, viewStart, viewEnd).toISO()!,
          end: clampISODate(e, viewStart, viewEnd).toISO()!
        });
      }
      cur = e;
    }
  }

  // filter display rows to seven planets (keep nodes out of UI if desired)
  const keep7 = (b: DashaBand) => (['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn'] as const).includes(b.lord as any);

  return {
    md: mdView.filter(keep7),
    ad: ad.filter(keep7),
    pd: pd.filter(keep7),
    range: { start: viewStart.toISO()!, end: viewEnd.toISO()! }
  };
}

/** Functional nature by Lagna (1=Aries .. 12=Pisces): benefic / neutral / malefic */
type FuncNature = 'benefic'|'neutral'|'malefic';
export type FuncMap = Record<DashaPlanet, FuncNature>;
// Extended FuncMap that includes nodes (Rahu/Ketu) - used by charts and dashboards
export type ExtendedFuncMap = Record<PlanetName, FuncNature>;

// Rahu and Ketu functional nature: Generally malefic across all lagnas
// (They take on the nature of their dispositors and associations, but default to malefic)
const NODES_FUNCTIONAL: Record<'Rahu'|'Ketu', FuncNature> = {
  Rahu: 'malefic',
  Ketu: 'malefic'
};

const FUNCTIONAL_BY_LAGNA: Record<number, FuncMap> = {
  // Aries (1): Sun-5L, Moon-4L, Mars-1L/8L (LAGNA LORD=BENEFIC), Mercury-3L/6L, Jupiter-9L/12L, Venus-2L/7L, Saturn-10L/11L
  1: { Sun:'benefic', Moon:'benefic', Mars:'benefic', Mercury:'malefic', Jupiter:'benefic', Venus:'neutral', Saturn:'malefic' },
  // Taurus (2): Sun-4L, Moon-3L, Mars-7L/12L, Mercury-2L/5L, Jupiter-8L/11L, Venus-1L/6L (LAGNA LORD=BENEFIC), Saturn-9L/10L (Yoga Karaka)
  2: { Sun:'benefic', Moon:'neutral', Mars:'malefic', Mercury:'benefic', Jupiter:'malefic', Venus:'benefic', Saturn:'benefic' },
  // Gemini (3): Sun-3L, Moon-2L, Mars-6L/11L, Mercury-1L/4L (LAGNA LORD=BENEFIC), Jupiter-7L/10L, Venus-5L/12L, Saturn-8L/9L
  3: { Sun:'malefic', Moon:'neutral', Mars:'malefic', Mercury:'benefic', Jupiter:'neutral', Venus:'benefic', Saturn:'benefic' },
  // Cancer (4): Sun-2L, Moon-1L (LAGNA LORD=BENEFIC), Mars-5L/10L (Yoga Karaka), Mercury-3L/12L, Jupiter-6L/9L, Venus-4L/11L, Saturn-7L/8L
  4: { Sun:'neutral', Moon:'benefic', Mars:'benefic', Mercury:'malefic', Jupiter:'benefic', Venus:'neutral', Saturn:'malefic' },
  // Leo (5): Sun-1L (LAGNA LORD=BENEFIC), Moon-12L, Mars-4L/9L (Yoga Karaka), Mercury-2L/11L, Jupiter-5L/8L, Venus-3L/10L, Saturn-6L/7L
  5: { Sun:'benefic', Moon:'neutral', Mars:'benefic', Mercury:'malefic', Jupiter:'benefic', Venus:'neutral', Saturn:'malefic' },
  // Virgo (6): Sun-12L, Moon-11L, Mars-3L/8L, Mercury-1L/10L (LAGNA LORD + Yoga Karaka=BENEFIC), Jupiter-4L/7L, Venus-2L/9L, Saturn-5L/6L
  6: { Sun:'malefic', Moon:'malefic', Mars:'malefic', Mercury:'benefic', Jupiter:'neutral', Venus:'benefic', Saturn:'neutral' },
  // Libra (7): Sun-11L, Moon-10L, Mars-2L/7L, Mercury-9L/12L, Jupiter-3L/6L, Venus-1L/8L (LAGNA LORD=BENEFIC), Saturn-4L/5L (Yoga Karaka)
  7: { Sun:'malefic', Moon:'neutral', Mars:'neutral', Mercury:'benefic', Jupiter:'malefic', Venus:'benefic', Saturn:'benefic' },
  // Scorpio (8): Sun-10L, Moon-9L, Mars-1L/6L (LAGNA LORD=BENEFIC), Mercury-8L/11L, Jupiter-2L/5L, Venus-7L/12L, Saturn-3L/4L
  8: { Sun:'benefic', Moon:'benefic', Mars:'benefic', Mercury:'malefic', Jupiter:'benefic', Venus:'malefic', Saturn:'neutral' },
  // Sagittarius (9): Sun-9L, Moon-8L, Mars-5L/12L, Mercury-7L/10L, Jupiter-1L/4L (LAGNA LORD=BENEFIC), Venus-6L/11L, Saturn-2L/3L
  9: { Sun:'benefic', Moon:'neutral', Mars:'benefic', Mercury:'neutral', Jupiter:'benefic', Venus:'malefic', Saturn:'malefic' },
  // Capricorn (10): Sun-8L, Moon-7L, Mars-4L/11L, Mercury-6L/9L, Jupiter-3L/12L, Venus-5L/10L (Yoga Karaka), Saturn-1L/2L (LAGNA LORD=BENEFIC)
 10: { Sun:'malefic', Moon:'neutral', Mars:'neutral', Mercury:'benefic', Jupiter:'malefic', Venus:'benefic', Saturn:'benefic' },
  // Aquarius (11): Sun-7L, Moon-6L, Mars-3L/10L, Mercury-5L/8L, Jupiter-2L/11L, Venus-4L/9L (Yoga Karaka), Saturn-1L/12L (LAGNA LORD=BENEFIC)
 11: { Sun:'neutral', Moon:'malefic', Mars:'neutral', Mercury:'benefic', Jupiter:'malefic', Venus:'benefic', Saturn:'benefic' },
  // Pisces (12): Sun-6L, Moon-5L, Mars-2L/9L, Mercury-4L/7L, Jupiter-1L/10L (LAGNA LORD=BENEFIC), Venus-3L/8L, Saturn-11L/12L
 12: { Sun:'malefic', Moon:'benefic', Mars:'benefic', Mercury:'neutral', Jupiter:'benefic', Venus:'malefic', Saturn:'malefic' },
};

export function classifyFunctionalForLagna(lagnaIndex1to12: number): ExtendedFuncMap {
  const base = FUNCTIONAL_BY_LAGNA[lagnaIndex1to12] ?? FUNCTIONAL_BY_LAGNA[1];
  // Add Rahu and Ketu classifications
  return { ...base, ...NODES_FUNCTIONAL } as ExtendedFuncMap;
}

/* === SHODASHAVARGA ENGINE START ==============================================
//
// Implements all 16 divisional charts (Shodashavarga).
// Notes on start rules (common Parāśari practice used here):
// - Modal group (D4, D9, D16, D20, D24): start by sign modality
//     movable: from the sign itself
//     fixed:   from 9th (sign + 8)
//     dual:    from 5th (sign + 4)
// - Odd/Even group (D7, D27, D40, D45, D60): odd signs count from Aries (1),
//   even signs count from Libra (7).
// - D10: odd signs start from the sign; even signs from 9th (sign + 8).
// - D12: 12 equal parts, counting from the sign itself.
// - D30 (Trimsamsa): unequal segments with rulers; for odd base signs take the
//   ruler’s ODD domicile, for even base signs take the ruler’s EVEN domicile.
//
// If your tradition uses a different scheme for a varga, you can swap the
// start rule functions below without changing call-sites.
//
// ============================================================================ */

export type VargaKey =
  | 'D1'  // Rāśi
  | 'D2'  // Hora
  | 'D3'  // Drekkana
  | 'D4'  // Chaturthamsa
  | 'D7'  // Saptamsa
  | 'D9'  // Navamsa
  | 'D10' // Dashamsa
  | 'D12' // Dwadasamsa
  | 'D16' // Shodasamsa
  | 'D20' // Vimsamsa
  | 'D24' // Chaturvimshamsa
  | 'D27' // Saptavimshamsa
  | 'D30' // Trimsamsa
  | 'D40' // Khavedamsa
  | 'D45' // Akshavedamsa
  | 'D60';// Shashtiamsa

// const norm360 = (x: number) => (x % 360 + 360) % 360;
const signFromLon = (lonDeg: number) => Math.floor(norm360(lonDeg) / 30) + 1; // 1..12
const inSignDeg = (lonDeg: number) => norm360(lonDeg) % 30;
const isOddSign = (s: number) => (s % 2) === 1;

const modalityOf = (s: number): 'movable'|'fixed'|'dual' => {
  const m = (s - 1) % 12;
  if ([0,3,6,9].includes(m))  return 'movable';
  if ([1,4,7,10].includes(m)) return 'fixed';
  return 'dual';
};

// ---- generic helpers ----
function startByModality(sign: number, offsets: { movable: number; fixed: number; dual: number }) {
  const mod = modalityOf(sign);
  const off = mod === 'movable' ? offsets.movable : mod === 'fixed' ? offsets.fixed : offsets.dual;
  return advanceSign(sign, off);
}
function startByParity(sign: number, oddStart: number, evenStart: number) {
  return isOddSign(sign) ? oddStart : evenStart;
}
function vargaByEqualParts(sign: number, degInSign: number, parts: number, startRule: (sign: number) => number) {
  const idx = Math.floor(degInSign / (30 / parts));
  const start = startRule(sign);
  return advanceSign(start, idx);
}

// ---- specific varga sign calculators ----

// D1 — base sign
function d1Sign(sign: number, _deg: number) {
  return sign;
}

// D2 — Hora
function d2Sign(sign: number, deg: number) {
  // Odd signs: 0–15° Leo(5), 15–30° Cancer(4)
  // Even signs: 0–15° Cancer(4), 15–30° Leo(5)
  const first = isOddSign(sign) ? 5 : 4;
  const second = isOddSign(sign) ? 4 : 5;
  return deg < 15 ? first : second;
}

// D3 — Drekkana
function d3Sign(sign: number, deg: number) {
  // All signs: 0–10° same sign; 10–20° 5th; 20–30° 9th
  const idx = Math.floor(deg / 10);
  const seq = [sign, advanceSign(sign, 4), advanceSign(sign, 8)];
  return seq[idx];
}

// D4 — Chaturthamsa (4 parts, advance by 3 signs per part - trines)
function d4Sign(sign: number, deg: number) {
  const idx = Math.floor(deg / 7.5);
  return advanceSign(sign, idx * 3);
}

// D7 — Saptamsa (odd: from sign; even: from 7th)
function d7Sign(sign: number, deg: number) {
  const part = 30 / 7;
  const idx = Math.floor(deg / part);
  const start = isOddSign(sign) ? sign : advanceSign(sign, 6);
  return advanceSign(start, idx);
}

// D9 – Navamsa (modal start: 0/8/4)
function d9Sign(sign: number, deg: number) {
  return vargaByEqualParts(sign, deg, 9, (s) => startByModality(s, { movable: 0, fixed: 8, dual: 4 }));
}

// D10 — Dashamsa (odd: from sign; even: from 9th)
function d10Sign(sign: number, deg: number) {
  const idx = Math.floor(deg / 3);
  const base = isOddSign(sign) ? sign : advanceSign(sign, 8);
  return advanceSign(base, idx);
}

// D12 – Dwadasamsa (always from sign)
function d12Sign(sign: number, deg: number) {
  const idx = Math.floor(deg / (30 / 12));
  return advanceSign(sign, idx);
}

// D16 — Shodasamsa (uses full longitude multiplication method)
function d16Sign(sign: number, deg: number) {
  const fullLon = (sign - 1) * 30 + deg;
  const vargaLon = norm360(fullLon * 16);
  return Math.floor(vargaLon / 30) + 1;
}

// D20 — Vimsamsa (uses full longitude multiplication method)
function d20Sign(sign: number, deg: number) {
  const fullLon = (sign - 1) * 30 + deg;
  const vargaLon = norm360(fullLon * 20);
  return Math.floor(vargaLon / 30) + 1;
}

// D24 — Chaturvimshamsa
// Standard Parashari formula (24 equal parts of each sign)
function d24Sign(sign: number, deg: number) {
  const fullLon = (sign - 1) * 30 + deg;
  const vargaLon = norm360(fullLon * 24);
  return Math.floor(vargaLon / 30) + 1;
}

// D27 — Saptavimshamsa (odd/even: Aries/Libra)
function d27Sign(sign: number, deg: number) {
  return vargaByEqualParts(sign, deg, 27, (s) => startByParity(s, 1 /*Aries*/, 7 /*Libra*/));
}

// D30 — Trimsamsa (unequal segments)
type TriSeg = { endDeg: number; ruler: 'Mars'|'Saturn'|'Jupiter'|'Mercury'|'Venus' };
const TRIM_ODD: TriSeg[]  = [
  { endDeg: 5,  ruler: 'Mars'    },
  { endDeg: 10, ruler: 'Saturn'  },
  { endDeg: 18, ruler: 'Jupiter' },
  { endDeg: 25, ruler: 'Mercury' },
  { endDeg: 30, ruler: 'Venus'   },
];
const TRIM_EVEN: TriSeg[] = [
  { endDeg: 5,  ruler: 'Saturn'  },
  { endDeg: 10, ruler: 'Mars'    },
  { endDeg: 18, ruler: 'Jupiter' },
  { endDeg: 25, ruler: 'Venus'   },
  { endDeg: 30, ruler: 'Mercury' },
];
const ODD_DOMICILE: Record<string, number> = {
  Mars: 1, Saturn: 11, Jupiter: 9, Mercury: 3, Venus: 7
};
const EVEN_DOMICILE: Record<string, number> = {
  Mars: 8, Saturn: 10, Jupiter: 12, Mercury: 6, Venus: 2
};
function d30Sign(sign: number, deg: number) {
  const segs = isOddSign(sign) ? TRIM_ODD : TRIM_EVEN;
  const seg = segs.find(s => deg < s.endDeg) || segs[segs.length - 1];
  const map = isOddSign(sign) ? ODD_DOMICILE : EVEN_DOMICILE;
  return map[seg.ruler];
}

// D40 — Khavedamsa (odd/even: Aries/Libra)
function d40Sign(sign: number, deg: number) {
  return vargaByEqualParts(sign, deg, 40, (s) => startByParity(s, 1, 7));
}

// D45 — Akshavedamsa (odd/even: Aries/Libra)
function d45Sign(sign: number, deg: number) {
  return vargaByEqualParts(sign, deg, 45, (s) => startByParity(s, 1, 7));
}

// D60 — Shashtiamsa (simple advancement with mod12 wrapping)
function d60Sign(sign: number, deg: number) {
  const idx = Math.floor(deg / 0.5); // 60 parts of 0.5° each
  return advanceSign(sign, idx % 12);
}

type VargaFn = (sign: number, degInSign: number) => number;

const VARGA_FNS: Record<VargaKey, VargaFn> = {
  D1:  d1Sign,
  D2:  d2Sign,
  D3:  d3Sign,
  D4:  d4Sign,
  D7:  d7Sign,
  D9:  d9Sign,
  D10: d10Sign,
  D12: d12Sign,
  D16: d16Sign,
  D20: d20Sign,
  D24: d24Sign,
  D27: d27Sign,
  D30: d30Sign,
  D40: d40Sign,
  D45: d45Sign,
  D60: d60Sign,
};

// --- helpers to compute degrees within a varga sign (for labels) ---
const VARGA_EQUAL_PARTS: Partial<Record<VargaKey, number>> = {
  D2: 2, D3: 3, D4: 4, D7: 7, D9: 9, D10: 10, D12: 12, D16: 16, D20: 20, D24: 24, D27: 27, D40: 40, D45: 45, D60: 60,
};

// For equal-part vargas: map position within the sub-part → 0..30° inside the varga sign.
function degInVargaEqualPart(degInSign: number, parts: number): number {
  const partSize = 30 / parts;                       // degrees wide in D1 sign
  const idx = Math.floor(degInSign / partSize);      // 0..parts-1
  const start = idx * partSize;
  const frac = (degInSign - start) / partSize;       // 0..1 inside that sub-part
  return frac * 30;                                   // scale to full varga sign span
}

// D30: unequal segments; scale inside the chosen segment to 0..30°
function degInVargaD30(sign: number, degInSign: number): number {
  const odd = (sign % 2) === 1;
  const segs = odd ? TRIM_ODD : TRIM_EVEN;           // from earlier table
  let start = 0, size = 0;
  for (const s of segs) {
    const end = s.endDeg;
    if (degInSign < end) { size = end - start; break; }
    start = end;
  }
  const inside = Math.max(0, Math.min(size || 1, degInSign - start));
  return (inside / (size || 1)) * 30;
}

/**
 * Degree-aware varga placements:
 * - Returns house like before, AND offsetDeg (0..29.99) inside that house for labels.
 */
export function vargaToPlacementsWithDegrees(
  vk: VargaKey,
  ascLonDeg: number,
  planetLongsSidereal: Record<PlanetName, number>
): ReturnType<typeof assignPlanetsToHouses> {
  const vargas = computeShodashavarga(ascLonDeg, planetLongsSidereal);
  const v = vargas[vk];
  const placements: any = {};
  (Object.keys(v.planetSigns) as PlanetName[]).forEach((p) => {
    const baseLon = planetLongsSidereal[p];            // 0..360, sidereal
    const baseSign = Math.floor(((baseLon % 360) + 360) % 360 / 30) + 1; // 1..12
    const degInSign = ((baseLon % 30) + 30) % 30;      // 0..30 inside the natal sign

    // compute degrees inside the varga sign
    let degVarga = 0;
    if (vk === 'D30') {
      degVarga = degInVargaD30(baseSign, degInSign);
    } else if (VARGA_EQUAL_PARTS[vk]) {
      degVarga = degInVargaEqualPart(degInSign, VARGA_EQUAL_PARTS[vk]!);
    } else {
      // D1 (or any fallback): keep natal inside-sign degrees
      degVarga = degInSign;
    }

    const sign = v.planetSigns[p];                     // varga sign 1..12
    const house = ((sign - v.ascSign + 12) % 12) + 1;  // house 1..12
    placements[p] = { house, offsetDeg: Math.max(0, Math.min(29.9999, degVarga)) };
  });
  return placements;
}

export type VargaResult = {
  ascSign: number; // 1..12
  planetSigns: Record<PlanetName, number>; // 1..12 per body
};

// Main API: compute all 16 vargas
export function computeShodashavarga(
  ascLonDeg: number,
  planetLongsSidereal: Record<PlanetName, number>
): Record<VargaKey, VargaResult> {
  const ascS = signFromLon(ascLonDeg);
  const ascD = inSignDeg(ascLonDeg);

  const out: Partial<Record<VargaKey, VargaResult>> = {};
  (Object.keys(VARGA_FNS) as VargaKey[]).forEach((vk) => {
    const f = VARGA_FNS[vk];
    const res: Record<PlanetName, number> = {} as any;

    (Object.keys(planetLongsSidereal) as PlanetName[]).forEach((p) => {
      const s = signFromLon(planetLongsSidereal[p]);
      const d = inSignDeg(planetLongsSidereal[p]);
      res[p] = f(s, d);
    });

    out[vk] = {
      ascSign: f(ascS, ascD),
      planetSigns: res,
    };
  });

  return out as Record<VargaKey, VargaResult>;
}

// Back-compat alias used by App.tsx in your current code
export function computeImplementedVargas(
  ascLonDeg: number,
  planetLongsSidereal: Record<PlanetName, number>
): Record<VargaKey, VargaResult> {
  return computeShodashavarga(ascLonDeg, planetLongsSidereal);
}

// Build placements for drawing a chosen divisional chart
export function vargaToPlacements(v: VargaResult): ReturnType<typeof assignPlanetsToHouses> {
  const placements: any = {};
  (Object.keys(v.planetSigns) as PlanetName[]).forEach((p) => {
    const sign = v.planetSigns[p]; // 1..12
    const house = ((sign - v.ascSign + 12) % 12) + 1; // 1..12
    placements[p] = { house, offsetDeg: 0 };
  });
  return placements;
}

// === SHODASHAVARGA ENGINE END =================================================

// END FILE: src/utils/astroEngine.ts
